import { ACCESS_TOKEN } from 'shared/constants/cookies';
import { cookies } from 'shared/utils/storage/cookies';
import { getParsedToken } from 'utils/misc/getParsedToken';
import {
  getStringValuesFromLocalStorage,
  localStorageAvailable,
  setInLocalStorage,
} from 'utils/storage';
import { v4 as uuidv4 } from 'uuid';

export const generateId = (prefix: string): string => {
  const newId = prefix + uuidv4();
  return newId;
};

export const CHAT_SESSION_ID_KEY = 'ndChatSessionId';
export const CHAT_USER_ID_KEY = 'ndChatUserId';
const CHAT_STATE_KEY = 'ndChatState';

export const getChatState = (): string | null => {
  const chatState = getStringValuesFromLocalStorage(CHAT_STATE_KEY);
  return chatState;
};

export const updateLocalStorageChatState = (state: boolean): void => {
  setInLocalStorage(CHAT_STATE_KEY, state.toString());
  if (!localStorageAvailable()) {
    return;
  } else {
    localStorage.setItem(CHAT_STATE_KEY, state.toString());
  }
};

export const updateLocalStorageChatUserId = (userId: string): void => {
  if (!localStorageAvailable()) {
    return;
  } else {
    const existingUserId = localStorage.getItem(CHAT_USER_ID_KEY);

    if (existingUserId) {
      localStorage.removeItem(CHAT_USER_ID_KEY);
    }
    localStorage.setItem(CHAT_USER_ID_KEY, userId);
  }
};

export const getChatUserId = (): string | undefined => {
  const token = cookies.get(ACCESS_TOKEN, true);
  let userId: string | null = null;

  if (token) {
    const parsedToken = getParsedToken();
    userId = 'user-' + parsedToken?.ciam_id;
  }

  const ndChatUserId = getStringValuesFromLocalStorage(CHAT_USER_ID_KEY);
  const { userIdFromUrl } = checkUrlParamsForCognigyIds();

  if (userIdFromUrl) {
    return userIdFromUrl;
  } else if (ndChatUserId && ndChatUserId === userId) {
    return ndChatUserId;
  } else {
    return userId ? userId : generateId('user-');
  }
};

export const updateLocalStorageChatSessionId = (sessionId: string): void => {
  if (!localStorageAvailable()) {
    return;
  } else {
    const existingSessionId = localStorage.getItem(CHAT_SESSION_ID_KEY);
    if (existingSessionId) {
      localStorage.removeItem(CHAT_SESSION_ID_KEY);
    }
    localStorage.setItem(CHAT_SESSION_ID_KEY, sessionId);
  }
};

export const generateSessionId = (): string => {
  const newSessionId = generateId('session-');
  return newSessionId;
};

export const getChatSessionId = (): string | undefined => {
  const ndChatSessionId = getStringValuesFromLocalStorage(CHAT_SESSION_ID_KEY);
  const { sessionIdFromUrl } = checkUrlParamsForCognigyIds();

  if (sessionIdFromUrl) {
    return sessionIdFromUrl;
  } else if (ndChatSessionId) {
    return ndChatSessionId;
  } else {
    return generateSessionId();
  }
};

export const checkUrlParamsForCognigyIds = (): {
  userIdFromUrl: string | null;
  sessionIdFromUrl: string | null;
} => {
  if (typeof window === 'undefined') {
    // Return null values if window is not defined (e.g., during SSR)
    return {
      userIdFromUrl: null,
      sessionIdFromUrl: null,
    };
  }
  const urlParams = new URLSearchParams(window.location.search);
  const userId = urlParams.get('userId')?.replace(/['"]/g, '') || null;
  const sessionId = urlParams.get('sessionId')?.replace(/['"]/g, '') || null;
  return {
    userIdFromUrl: userId ? userId : null,
    sessionIdFromUrl: sessionId ? sessionId : null,
  };
};

export const isTimestampWithin72Hours = (timestamp: number): boolean => {
  const now = Date.now(); // Current timestamp in milliseconds
  const hours72InMilliseconds = 72 * 60 * 60 * 1000; // 72 hours in milliseconds
  return now - timestamp < hours72InMilliseconds; // Return true if within 72 hours
};

export const findLocalStorageKey = (knownParts: string[]) => {
  const keys = Object.keys(localStorage); // Get all keys in localStorage
  for (const key of keys) {
    try {
      const parsedKey = JSON.parse(key); // Parse the key as JSON
      if (
        Array.isArray(parsedKey) &&
        parsedKey[0] === knownParts[0] && // Match "webchat-client"
        parsedKey[1] === knownParts[1] && // Match user ID
        parsedKey[2] === knownParts[2] // Match session ID
      ) {
        return key; // Return the matching key
      }
    } catch (error) {
      // Ignore keys that are not valid JSON
    }
  }
  return null;
};

export const triggerTypes: Record<TriggerType, TriggerType> = {
  VoiceNudge: 'VoiceNudge',
  Selfcare: 'Selfcare',
};
export type TriggerType = 'VoiceNudge' | 'Selfcare';
